// components/EditItemModal.js
import React, { useState, useEffect } from "react"; // Add useEffect import
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Typography,
  Switch,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const EditItemModal = ({ open, onClose, item, categories, onSave, loading }) => {
  const [formData, setFormData] = useState({});

  // Reset form data when item changes
  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title || "",
        brand: item.brand || "",
        vendor: item.vendor || "",
        categoryId: item.categoryId || "",
        units: item.units || "",
        availableQuantity: item.availableQuantity || "",
        price: item.price || "",
        soldQuantity: item.soldQuantity || "",
        isActive: item.isActive !== undefined ? item.isActive : true,
      });
    }
  }, [item]); // This runs whenever the item prop changes

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = () => {
    // Convert numeric fields to numbers
    const formattedData = {
      ...formData,
      availableQuantity: Number(formData.availableQuantity) || 0,
      price: Number(formData.price) || 0,
      soldQuantity: Number(formData.soldQuantity) || 0,
      isActive: formData.isActive,
    };
    onSave(formattedData);
  };

  if (!item) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, pr: 6, fontWeight: 600, position: 'relative' }}>
        Edit Item: <strong>{item.title}</strong>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: 'text.secondary',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title || ""}
              onChange={(e) => handleChange("title", e.target.value)}
              margin="normal"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Brand"
              value={formData.brand || ""}
              onChange={(e) => handleChange("brand", e.target.value)}
              margin="normal"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Vendor"
              value={formData.vendor || ""}
              onChange={(e) => handleChange("vendor", e.target.value)}
              margin="normal"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Category"
              value={formData.categoryId || ""}
              onChange={(e) => handleChange("categoryId", e.target.value)}
              margin="normal"
              variant="outlined"
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.category}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Units (e.g., kg, pcs, ml)"
              value={formData.units || ""}
              onChange={(e) => handleChange("units", e.target.value)}
              margin="normal"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Available Quantity"
              type="number"
              inputProps={{ min: 0 }}
              value={formData.availableQuantity || ""}
              onChange={(e) => handleChange("availableQuantity", e.target.value)}
              margin="normal"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Price per Unit"
              type="number"
              inputProps={{ min: 0, step: "0.01" }}
              InputProps={{ startAdornment: "£ " }}
              value={formData.price || ""}
              onChange={(e) => handleChange("price", e.target.value)}
              margin="normal"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Sold Quantity"
              type="number"
              inputProps={{ min: 0 }}
              value={formData.soldQuantity || ""}
              onChange={(e) => handleChange("soldQuantity", e.target.value)}
              margin="normal"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive !== undefined ? formData.isActive : true}
                  onChange={(e) => handleChange("isActive", e.target.checked)}
                  color="success"
                />
              }
              label="Show in Mobile"
              sx={{ mt: 1 }}
            />
          </Grid>

          {/* Display current calculated values */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
              Current Values:
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={4}>
                <Typography variant="body2">
                  Stock Value: <strong>£ {(item.price * item.availableQuantity).toFixed(2)}</strong>
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  Last Updated:{" "}
                  {item.updatedAt
                    ? new Date(item.updatedAt.seconds * 1000).toLocaleDateString()
                    : "N/A"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? <CircularProgress size={24} /> : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditItemModal;